import { FocusEvent, ForwardedRef, forwardRef, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { DataTestId, FeatureFlag } from '@mews-commander/enums';
import { getToken } from '@optimus-web/common';
import { useFeatureFlag } from '@mews/framework';
import { getIsMql, transformAutocompleteCasesWithInputCases } from '../MqlHelper';
import { useSuggestionByIndex } from '../hooks/useSuggestionByIndex';
import { ExtendedTextInputContainer, FirstSuggestion, Overlay, SearchInput, TextLengthStack } from './styles';
import { KeywordColor } from './KeywordColor';
import { getKeywordColorArray } from './ExtendedTextInputHelper';
import { ClearSelectionButton } from './ClearSelectionButton';

interface ExtendedTextInputProps {
    focused: boolean;
    placeholder?: string;
    input?: string;
    keywordSelectedIndex?: number;
    keywordColors?: KeywordColor[];
    enterpriseIds?: string[];
    onFocus: (event: FocusEvent) => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    setIsMql: (isMql: boolean) => void;
    handleClearSelection: () => void;
}

const renderOverlay = (keywordColorArray: KeywordColor[] | undefined) =>
    keywordColorArray?.map(({ keyword, color, backgroundColor }, index) => (
        <span key={`${keyword}${color}`}>
            <span
                style={{
                    color,
                    backgroundColor,
                    borderRadius: '2px',
                }}
            >
                {keyword}
            </span>
            <span>{index === keywordColorArray.length - 1 ? '' : ' '}</span>
        </span>
    ));

export const ExtendedTextInput = forwardRef(
    (
        {
            focused,
            onChange,
            onFocus,
            onKeyUp,
            onKeyDown,
            placeholder,
            input,
            keywordSelectedIndex,
            keywordColors,
            enterpriseIds,
            setIsMql,
            handleClearSelection,
        }: ExtendedTextInputProps,
        searchRef: ForwardedRef<HTMLInputElement>
    ) => {
        const assistantAutocompleteEnabled = useFeatureFlag(FeatureFlag.assistantAutocompleteEnabled);
        const theme = useTheme();
        const defaultColor = getToken('textStaticPrimary')({ theme });
        const keywordColorArray = useMemo(
            () => getKeywordColorArray(input, keywordColors, defaultColor),
            [input, keywordColors, defaultColor]
        );

        const isMql = useMemo(() => getIsMql(input, keywordColors), [input, keywordColors]);
        setIsMql(isMql);

        const firstSuggestionText = useSuggestionByIndex(
            isMql && assistantAutocompleteEnabled,
            keywordSelectedIndex ?? 0,
            input,
            enterpriseIds
        );

        const transformedFirstSuggestionText = transformAutocompleteCasesWithInputCases(input, firstSuggestionText);

        return (
            <ExtendedTextInputContainer>
                {assistantAutocompleteEnabled && transformedFirstSuggestionText && (
                    <FirstSuggestion>{transformedFirstSuggestionText}</FirstSuggestion>
                )}

                <SearchInput
                    focused={focused}
                    isMql={isMql}
                    data-test-id={DataTestId.AssistantNavbarSearch}
                    ref={searchRef}
                    type="text"
                    placeholder={placeholder}
                    value={input}
                    onFocus={onFocus}
                    onChange={onChange}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                />
                {focused && input && (
                    <TextLengthStack spacing="100">
                        <span style={{ visibility: 'hidden', whiteSpace: 'pre' }}>
                            {isMql && assistantAutocompleteEnabled ? firstSuggestionText : input}
                        </span>
                        <ClearSelectionButton handleClick={handleClearSelection} />
                    </TextLengthStack>
                )}
                {assistantAutocompleteEnabled && isMql && <Overlay>{renderOverlay(keywordColorArray)}</Overlay>}
            </ExtendedTextInputContainer>
        );
    }
);
